import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { isSignedIn } from '../auth';
import { Logo } from '../components/Logo';

function Qr({ url, size = 140 }: { url: string; size?: number }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    void QRCode.toDataURL(url, { margin: 1, width: size * 2 }).then(setSrc);
  }, [url, size]);
  return src ? <img src={src} width={size} height={size} alt={`QR code for ${url}`} /> : <div style={{ width: size, height: size }} />;
}

/** Help for race directors, and a card to print for camera volunteers. */
export function HelpPage() {
  const origin = window.location.origin;
  return (
    <>
      <header className="bar no-print">
        <div className="bar-inner">
          <Link to={isSignedIn() ? '/' : '/login'} className="brand">
            <Logo />
            <span>Splitline</span>
          </Link>
        </div>
      </header>
      <main className="page" style={{ gap: 20, maxWidth: 900 }}>
        <section className="page-head no-print">
          <div>
            <h1>Running a race with Splitline</h1>
            <p>
              The short version. Print the volunteer card at the bottom and hand one to each camera volunteer. The <Link to="/docs">full guide</Link> covers every step in detail.
            </p>
          </div>
          <button type="button" className="btn" onClick={() => window.print()}>
            Print volunteer card
          </button>
        </section>

        <section className="card stack no-print">
          <h2>The week before</h2>
          <ol className="help-list">
            <li>
              <b>Create the race</b> and add timing points (at least a finish; a start if you want net times; splits optional).
            </li>
            <li>
              <b>Import the roster</b>: the participant export from your registration site (CSV or Excel) with bib, name and division.
            </li>
            <li>
              <b>Add camera stations</b> on Course &amp; stations. At the finish: one <i>timing</i> phone in line with the finish line, and one <i>bib</i> phone facing runners a few metres before it. Over ~150 runners, add a second timing phone on the other side of the line.
            </li>
            <li>
              <b>Ask volunteers to run the device check</b> on their phones: <span className="mono">{origin}/station/check</span>. It takes 30 seconds and tells them if the phone is up to it.
            </li>
            <li>
              <b>Do a rehearsal</b>: pair a phone, set the line, walk across it a few times and check the times appear on the dashboard. Clear the gun time afterwards.
            </li>
          </ol>
        </section>

        <section className="card stack no-print">
          <h2>Race morning</h2>
          <ol className="help-list">
            <li>Mark the finish line on the ground (tape, chalk or a mat) and put a cone at the far end.</li>
            <li>
              Show each volunteer their station’s pairing code or QR (Course &amp; stations). They pair, set the orange line on the marked line, do the cone check and press <b>Start recording</b> well before the first runner.
            </li>
            <li>
              Watch the <b>Race-day checklist</b> on the dashboard until everything is green: paired, line set, clock synced, recording.
            </li>
            <li>
              Press <b>Fire the gun now</b> the moment the race starts. If you were late, use <b>Adjust gun time</b> afterwards.
            </li>
          </ol>
        </section>

        <section className="card stack no-print">
          <h2>During and after</h2>
          <ol className="help-list">
            <li>Times appear on the dashboard within a minute or two. A backlog is normal when a pack finishes; it catches up.</li>
            <li>
              On <b>Results &amp; review</b>, work through flagged crossings: confirm the suggested runner, pick another, or dismiss non-runners. For anyone with no finish time, use <b>Find</b> (the model nominates likely frames) or <b>Enter time</b> from a hand-timed backup.
            </li>
            <li>
              Volunteers keep the page open until “waiting to upload” reaches 0. Then close the race on Course &amp; stations (this stops uploads) and the results are final.
            </li>
            <li>
              Turn on <b>public results</b> to get a link and QR code for runners; export CSV for your results site.
            </li>
          </ol>
          <p className="small muted" style={{ margin: 0 }}>
            Keep a hand-timed backup (a stopwatch and someone writing bib numbers at the finish) for your first few races. It’s the fallback for anyone the cameras miss.
          </p>
        </section>

        <section className="print-card card stack" aria-label="Volunteer card">
          <div className="inline" style={{ gap: 10 }}>
            <Logo />
            <span className="display" style={{ fontSize: 24 }}>
              Splitline camera volunteer
            </span>
          </div>
          <div className="qr-row">
            <div className="qr-cell">
              <Qr url={`${origin}/station/check`} />
              <div>
                <b>Before race day</b>
                <div className="small">Check your phone (30 s)</div>
                <div className="small mono">{origin.replace(/^https?:\/\//, '')}/station/check</div>
              </div>
            </div>
            <div className="qr-cell">
              <Qr url={`${origin}/station`} />
              <div>
                <b>On race day</b>
                <div className="small">Pair with the code from the race director</div>
                <div className="small mono">{origin.replace(/^https?:\/\//, '')}/station</div>
              </div>
            </div>
          </div>
          <ol className="help-list">
            <li>
              <b>Bring:</b> a charged phone, a battery pack and cable, a tripod or clamp.
            </li>
            <li>
              <b>Before you start:</b> turn on Do Not Disturb, close other apps, and lock the phone to the Splitline page: <b>Guided Access</b> on iPhone (triple-click the side button), <b>App pinning</b> on Android. The station page shows how.
            </li>
            <li>
              <b>Timing phone:</b> stand it exactly in line with the finish line, off the side of the course, 3–6 m from the nearest runner. Line it up so the far cone’s base and top both sit on the orange line.
            </li>
            <li>
              <b>Bib phone:</b> face runners head-on, a few metres before the line, within about 4 m of them (7 m on the 2× lens), chest height.
            </li>
            <li>
              Drag the orange line onto the finish line, press <b>Start recording</b>, and leave the phone alone. Recording pauses if the screen locks or you switch apps.
            </li>
            <li>
              <b>After the last runner:</b> hold the stop button for 3 s, then keep the page open until “waiting to upload” shows 0.
            </li>
          </ol>
        </section>
      </main>
    </>
  );
}
